import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
  ChangeDetectorRef,
  Output,
  EventEmitter,
} from '@angular/core';
import { transition, style, animate, trigger, AnimationEvent } from '@angular/animations';
import { Overlay, OverlayConfig, OverlayRef, GlobalPositionStrategy } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { FocusTrapFactory } from '@angular/cdk/a11y';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { PhxDialogPlacement } from './phx-dialog.types';

@Component({
  selector: 'phx-dialog',
  templateUrl: './phx-dialog.component.html',
  styleUrls: ['./phx-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  exportAs: 'phxDialog',
  host: {
    class: 'phx-dialog',
  },
  animations: [
    trigger('dialogAnimation', [
      transition(':enter, * => visible', [
        style({ opacity: 0, transform: 'translate{{ orientation }}({{ translation }}) scale({{ scalingFactor }})' }), // Initial state (hidden)
        animate('0.2s ease-in', style({ transform: 'translate{{ orientation }}(0) scale(100%)', opacity: 1 })), // Enter or become visible animation
      ]),
      transition('* => hidden', [
        animate('0.2s ease-out', style({ transform: 'translate{{ orientation }}({{ translation }}) scale({{ scalingFactor }})', opacity: 0 })), // Leave or become hidden animation
      ]),
    ]),
  ],
})
export class PhxDialogComponent implements OnChanges {
  /**
   * Indicates whether the dialog is open or not.
   */
  @Input() phxOpen: boolean = false;

  /**
   * Indicates whether the dialog should occupy full height.
   */
  @Input() phxFullHeight: boolean = false;

  /**
   * Indicates whether the dialog should occupy full width.
   */
  @Input() phxFullWidth: boolean = false;

  /**
   * Indicates whether the dialog should have a backdrop.
   */
  @Input() phxBackdrop: boolean = true;

  /**
   * The width of the dialog. Can be a string or number.
   * Example values: '300px', '50%', 300, 80vw
   */
  @Input() phxWidth: string | number = null;

  /**
   * The placement of the dialog. The dialog will animate based on the placement.
   * For example, if the placement is center the dialog will scale from 95% to 100% and fade in.
   * If the placement is left, the dialog will slide in from the left and same for the right, top, and bottom.
   * Possible values: 'center', 'left', 'right', 'top', 'bottom'
   */
  @Input() phxPlacement: PhxDialogPlacement = 'center';

  /**
   * The offset of the dialog from the top of the screen.
   * Example values: '300px', '50vh', '2rem'
   */
  @Input() phxOffset?: string = '';

  /**
   * The CSS class to apply to the overlay backdrop.
   */
  @Input() phxBackdropClass: string = '';

  /**
   * Event emitted when the `phxOpen` property changes.
   * Emits a boolean value indicating whether the dialog is open or not.
   */
  @Output() phxOpenChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * Event emitted when the backdrop is clicked.
   * Emits void.
   */
  @Output() phxBackdropClicked: EventEmitter<void> = new EventEmitter<void>();

  /**
   * Event emitted when a keydown event occurs.
   * Emits the KeyboardEvent object.
   */
  @Output() phxKeydown: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>();

  @ViewChild('dialogTemplate') public dialogTemplate: TemplateRef<HTMLElement>;
  public animationState: string = 'hidden';
  public animationOrientation: string = '';
  public animationTranslation: string = '0';
  public animationScalingFactor: string = '95%';

  private _phxOpen$ = new BehaviorSubject<boolean>(false);
  private _overlayRef?: OverlayRef;
  private _destroy$: Subject<void> = new Subject();

  constructor(
    private _viewContainer: ViewContainerRef,
    private _overlay: Overlay,
    private _cdr: ChangeDetectorRef,
    private _focusTrapFactory: FocusTrapFactory
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    const { phxOpen, phxPlacement } = changes;

    if (phxOpen) {
      this._phxOpen$.next(this.phxOpen);
    }

    if (phxPlacement) {
      this.setAnimationConfigBasedOnPlacement();
      this._overlayRef?.updatePositionStrategy(this.positionStrategy);
    }
  }

  public ngAfterViewInit(): void {
    this._phxOpen$.pipe(takeUntil(this._destroy$)).subscribe((isOpen: boolean) => {
      if (isOpen) {
        this.openOverlay();
      } else if (this._overlayRef) {
        this.closeOverlay();
      }
      this.phxOpenChange.emit(isOpen);
    });
  }

  public ngOnDestroy(): void {
    if (this._overlayRef) {
      this._overlayRef.dispose();
    }
    this._destroy$.next();
    this._destroy$.complete();
  }

  public openOverlay(): void {
    this.animationState = 'visible';

    const overlayConfig = new OverlayConfig({
      hasBackdrop: this.phxBackdrop,
      backdropClass: ['phx-dialog-backdrop', this.phxBackdropClass],
      positionStrategy: this.positionStrategy,
      scrollStrategy: this._overlay.scrollStrategies.block(),
      ...(!!this.phxWidth && { width: this.phxWidth }),
      ...(this.phxFullHeight && { height: '100%' }),
      ...(this.phxFullWidth && { width: '100%' }),
    });

    this._overlayRef = this._overlay.create(overlayConfig);
    this._overlayRef.attach(new TemplatePortal(this.dialogTemplate, this._viewContainer));
    this._overlayRef
      .backdropClick()
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.phxBackdropClicked.emit();
      });
  }

  public closeOverlay(): void {
    this.animationState = 'hidden';
    this._overlayRef?.backdropElement?.classList.remove('cdk-overlay-backdrop-showing');
    this._cdr.markForCheck();
  }

  public onAnimationEnd(event: AnimationEvent): void {
    if (event.toState === 'hidden') this._overlayRef.dispose();

    // We need to wait for the animation to end and then trap focus when the dialog becomes visible.
    if (event.toState === 'visible') {
      const dialogElement = event.element;
      const autofocusElement = dialogElement.querySelector('[autofocus]') as HTMLElement;
      if (autofocusElement) autofocusElement.focus();
      else dialogElement.focus();

      this._focusTrapFactory.create(dialogElement);
    }
  }

  private get positionStrategy(): GlobalPositionStrategy {
    if (this.phxOffset && this.phxPlacement === 'center') return this._overlay.position().global().centerHorizontally().top(this.phxOffset);
    if (this.phxPlacement === 'center') return this._overlay.position().global().centerHorizontally().centerVertically();
    return this._overlay.position().global()[this.phxPlacement]('0');
  }

  private getAnimationOrientation(): string {
    if (this.phxPlacement === 'left' || this.phxPlacement === 'right') return 'X';
    if (this.phxPlacement === 'top' || this.phxPlacement === 'bottom') return 'Y';
    return '';
  }

  private getAnimationTranslation(): string {
    if (this.phxPlacement === 'left' || this.phxPlacement === 'top') return '-100%';
    if (this.phxPlacement === 'right' || this.phxPlacement === 'bottom') return '100%';
    return '0';
  }

  private getAnimationScalingFactor(): string {
    if (this.phxPlacement === 'center') return '95%';
    return '100%';
  }

  private setAnimationConfigBasedOnPlacement(): void {
    this.animationOrientation = this.getAnimationOrientation();
    this.animationTranslation = this.getAnimationTranslation();
    this.animationScalingFactor = this.getAnimationScalingFactor();
  }
}
